// Dish & category photos, shared by the TVs and the admin.
// The library (web/photos/photos.json) gives every restaurant the same photo for the same dish, so nobody
// has to upload anything. A dish's own image_url / a category's photo_url always wins:
//   null            → automatic (from the library)
//   'none'          → no photo
//   any other text  → that photo (an uploaded photo, or a library photo picked in the admin: /photos/<id>.jpg)

export const NO_PHOTO = 'none';

const norm = (s) => String(s || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim();
const words = (s) => ` ${norm(s)} `;

export const libraryUrl = (id) => `/photos/${id}.jpg`;
export const libraryThumbUrl = (id) => `/photos/thumbs/${id}.jpg`;

/** The library photo behind a /photos/<id>.jpg address, if it is one. */
export function libraryPhotoByUrl(url, lib) {
  const m = /^\/photos\/([a-z0-9-]+)\.jpg$/.exec(url || '');
  return m && lib ? (lib.photos || []).find((p) => p.id === m[1]) || null : null;
}

function asPhoto(p, auto) {
  return { full: libraryUrl(p.id), thumb: libraryThumbUrl(p.id), title: p.title, id: p.id, auto };
}

function ownPhoto(url, lib) {
  const p = libraryPhotoByUrl(url, lib);
  return p ? asPhoto(p, false) : { full: url, thumb: url, title: 'Your photo', auto: false };
}

/** Library photo for a dish name: an exact name first, then a keyword the name contains. */
export function libraryPhotoForDish(name, lib) {
  if (!lib || !name) return null;
  const n = norm(name);
  const photos = lib.photos || [];
  const exact = photos.find((p) => (p.dishes || []).some((d) => norm(d) === n));
  if (exact) return exact;
  const w = words(name);
  return photos.find((p) => (p.keywords || []).some((k) => w.includes(` ${norm(k)} `))) || null;
}

/** { full, thumb, title, auto } for a menu item, or null for no photo. */
export function dishPhoto(item, lib) {
  if (item.image_url === NO_PHOTO) return null;
  if (item.image_url) return ownPhoto(item.image_url, lib);
  const p = libraryPhotoForDish(item.name, lib);
  return p ? asPhoto(p, true) : null;
}

/** The library's photo set for a category name (e.g. "Appetizers" → samosa, kachori, pakora). */
export function libraryPhotosForCategory(name, lib) {
  if (!lib || !name) return [];
  const w = words(name);
  const set = (lib.categories || []).find((c) => (c.keywords || []).some((k) => w.includes(` ${norm(k)} `)));
  if (!set) return [];
  return set.photos.map((id) => (lib.photos || []).find((p) => p.id === id)).filter(Boolean);
}

/** Photos shown with a category: its own photo if one was picked, otherwise the library's set. */
export function categoryPhotos(cat, lib) {
  if (cat.photo_url === NO_PHOTO) return [];
  if (cat.photo_url) return [ownPhoto(cat.photo_url, lib)];
  return libraryPhotosForCategory(cat.name, lib).map((p) => asPhoto(p, true));
}

/** The big photo for the lunch / dinner showcase slide. */
export function periodPhoto(period, lib) {
  const id = lib && lib.periods && lib.periods[period];
  const p = id && (lib.photos || []).find((x) => x.id === id);
  return p ? asPhoto(p, true) : null;
}
